/**
 * Utility functions for handling API error responses
 */

/**
 * Extract a user-friendly error message from an API error response
 * @param {Error} error - The error object from axios
 * @returns {string} - A formatted error message
 */
export const getErrorMessage = (error) => {
  // Check if there's a response from the server
  if (!error.response) {
    return error.message || 'Network error - please check your connection';
  }

  const errorData = error.response?.data?.detail;

  // Handle different error formats from FastAPI
  if (typeof errorData === 'string') {
    return errorData;
  }

  if (Array.isArray(errorData)) {
    // Handle validation error array from FastAPI/Pydantic
    const messages = errorData.map(err => {
      if (typeof err === 'string') {
        return err;
      }
      // Handle Pydantic validation error format
      if (err.msg && err.loc) {
        const field = err.loc[err.loc.length - 1];
        return `${field}: ${err.msg}`;
      }
      return err.msg || err.message || 'Validation error';
    });
    return messages.join(', ');
  }

  if (errorData && typeof errorData === 'object') {
    // Handle single validation error object
    if (errorData.msg && errorData.loc) {
      const field = errorData.loc[errorData.loc.length - 1];
      return `${field}: ${errorData.msg}`;
    }
    return errorData.msg || errorData.message || 'Request failed';
  }

  // Check for generic error message in response
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Fallback to status-based messages
  switch (error.response?.status) {
    case 400:
      return error.response?.data?.detail || 'Bad request - please check your input';
    case 401:
      return 'Invalid email or password';
    case 403:
      return 'Access denied - you do not have permission for this action';
    case 404:
      return 'Resource not found';
    case 422:
      return 'Validation error - please check your input';
    case 500:
      return 'Server error - please try again later';
    default:
      return error.message || 'An error occurred';
  }
};

/**
 * Format field-specific errors for form display
 * @param {Error} error - The error object from axios
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const getFieldErrors = (error) => {
  const fieldErrors = {};
  
  if (!error.response?.data?.detail || !Array.isArray(error.response.data.detail)) {
    return fieldErrors;
  }

  error.response.data.detail.forEach(err => {
    if (err.loc && err.loc.length > 0 && err.msg) {
      const fieldName = err.loc[err.loc.length - 1];
      fieldErrors[fieldName] = err.msg;
    }
  });

  return fieldErrors;
};